/**
 * useTouchGestures Hook
 * 
 * Provides swipe gestures, pinch-to-zoom, and pull-to-refresh functionality.
 * 
 * @module useTouchGestures
 */

import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook for touch gesture handling
 * 
 * @param {Object} [options] - Configuration options
 * @param {number} [options.minSwipeDistance=50] - Minimum swipe distance in pixels
 * @param {number} [options.maxSwipeTime=300] - Maximum swipe time in milliseconds
 * @param {Function} [options.onSwipeLeft] - Callback for left swipe
 * @param {Function} [options.onSwipeRight] - Callback for right swipe
 * @param {Function} [options.onSwipeUp] - Callback for up swipe
 * @param {Function} [options.onSwipeDown] - Callback for down swipe
 * @param {Function} [options.onPinch] - Callback for pinch gesture
 * @param {Function} [options.onPullToRefresh] - Callback for pull-to-refresh
 * @returns {Object} Touch handlers and state
 */
export const useTouchGestures = (options = {}) => {
  const {
    minSwipeDistance = 50,
    maxSwipeTime = 300,
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onPinch,
    onPullToRefresh
  } = options;

  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const touchStartTimeRef = useRef(null);
  const initialDistanceRef = useRef(null);
  const pullDistanceRef = useRef(0);

  /**
   * Calculate distance between two touch points
   * 
   * @param {Touch} touch1 - First touch
   * @param {Touch} touch2 - Second touch
   * @returns {number} Distance in pixels
   */
  const getDistance = useCallback((touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  /**
   * Handle touch start
   */
  const handleTouchStart = useCallback((e) => {
    touchStartTimeRef.current = Date.now();
    
    if (e.touches.length === 1) {
      // Single touch - swipe or pull-to-refresh
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
      pullDistanceRef.current = 0;
    } else if (e.touches.length === 2 && onPinch) {
      // Two touches - pinch gesture
      initialDistanceRef.current = getDistance(e.touches[0], e.touches[1]);
    }
  }, [onPinch, getDistance]);

  /**
   * Handle touch move
   */
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && touchStartRef.current) {
      const currentY = e.touches[0].clientY;
      const startY = touchStartRef.current.y;
      
      // Pull-to-refresh detection (only if at top of page)
      if (window.scrollY === 0 && currentY > startY && onPullToRefresh) {
        pullDistanceRef.current = currentY - startY;
        
        // Prevent default scrolling during pull-to-refresh
        if (pullDistanceRef.current > 10) {
          e.preventDefault();
        }
      }
    } else if (e.touches.length === 2 && initialDistanceRef.current && onPinch) {
      // Pinch gesture
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / initialDistanceRef.current;
      
      onPinch(scale, {
        centerX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        centerY: (e.touches[0].clientY + e.touches[1].clientY) / 2
      });
    }
  }, [onPullToRefresh, onPinch, getDistance]);

  /**
   * Handle touch end
   */
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current || !touchStartTimeRef.current) return;

    touchEndRef.current = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const swipeTime = Date.now() - touchStartTimeRef.current;
    const distanceX = touchEndRef.current.x - touchStartRef.current.x;
    const distanceY = touchEndRef.current.y - touchStartRef.current.y;
    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

    // Check if it's a valid swipe
    if (swipeTime <= maxSwipeTime && distance >= minSwipeDistance) {
      const absX = Math.abs(distanceX);
      const absY = Math.abs(distanceY);

      // Determine swipe direction
      if (absX > absY) {
        // Horizontal swipe
        if (distanceX > 0 && onSwipeRight) {
          onSwipeRight();
        } else if (distanceX < 0 && onSwipeLeft) {
          onSwipeLeft();
        }
      } else {
        // Vertical swipe
        if (distanceY > 0 && onSwipeDown) {
          onSwipeDown();
        } else if (distanceY < 0 && onSwipeUp) {
          onSwipeUp();
        }
      }
    }

    // Check for pull-to-refresh
    if (pullDistanceRef.current > 80 && onPullToRefresh) {
      onPullToRefresh();
    }

    // Reset
    touchStartRef.current = null;
    touchEndRef.current = null;
    touchStartTimeRef.current = null;
    initialDistanceRef.current = null;
    pullDistanceRef.current = 0;
  }, [minSwipeDistance, maxSwipeTime, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onPullToRefresh]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    pullDistance: pullDistanceRef.current
  };
};

